<template>
  <div class="table-pager">
    <div class="table-pager__info">
      <label class="table-pager__size">
        <span>Voir</span>
        <select
          class="form-select form-select-sm"
          :value="perPage"
          @change="onPerPage"
        >
          <option v-for="n in options" :key="n" :value="n">{{ n }}</option>
        </select>
      </label>
      <span class="table-pager__count">
        <template v-if="pagination.total">
          <b>{{ pagination.from }}–{{ pagination.to }}</b> sur
          <b>{{ pagination.total }}</b>
        </template>
        <template v-else>0 résultat</template>
      </span>
    </div>

    <nav v-if="pagination.last_page > 1" aria-label="Pagination">
      <ul class="pagination mb-0">
        <li class="page-item">
          <a
            class="page-link"
            :class="{ disabled: pagination.current_page <= 1 }"
            href="#"
            aria-label="Précédent"
            @click.prevent="go(pagination.current_page - 1)"
          >
            <i class="flaticon-chevron-1"></i>
          </a>
        </li>

        <li
          v-for="(item, i) in items"
          :key="i"
          class="page-item table-pager__page"
          :class="{ 'table-pager__page--current': item === pagination.current_page }"
        >
          <span v-if="item === '…'" class="page-link table-pager__gap">…</span>
          <a
            v-else
            class="page-link"
            :class="{ active: item === pagination.current_page }"
            href="#"
            @click.prevent="go(item)"
          >
            {{ item }}
          </a>
        </li>

        <!-- phones: the numbers are hidden, keep a "page x / y" indicator -->
        <li class="page-item table-pager__mobile">
          <span class="page-link">
            {{ pagination.current_page }} / {{ pagination.last_page }}
          </span>
        </li>

        <li class="page-item">
          <a
            class="page-link"
            :class="{ disabled: pagination.current_page >= pagination.last_page }"
            href="#"
            aria-label="Suivant"
            @click.prevent="go(pagination.current_page + 1)"
          >
            <i class="flaticon-chevron"></i>
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";

interface Pagination {
  current_page: number;
  last_page: number;
  from: number;
  to: number;
  total: number;
}

// Compact, responsive footer for a server-paginated table: page size, "11–20 sur 177"
// and a page window (1 … 4 5 6 … 18). Same events as <PaginatorContent>:
// `@paginate` gives the page, `v-model:per-page` the page size.
export default defineComponent({
  name: "TablePager",
  props: {
    pagination: { type: Object as PropType<Pagination>, required: true },
    perPage: { type: Number, default: 10 },
    options: { type: Array as PropType<number[]>, default: () => [10, 25, 50, 100] },
  },
  emits: ["paginate", "update:perPage"],
  setup(props, { emit }) {
    const items = computed<(number | "…")[]>(() => {
      const last = props.pagination.last_page;
      const cur = props.pagination.current_page;
      const pages = new Set<number>([1, last, cur - 1, cur, cur + 1]);
      const sorted = [...pages].filter((p) => p >= 1 && p <= last).sort((a, b) => a - b);
      const out: (number | "…")[] = [];
      sorted.forEach((p, i) => {
        if (i && p - sorted[i - 1] > 1) out.push("…");
        out.push(p);
      });
      return out;
    });

    function go(page: number) {
      if (page < 1 || page > props.pagination.last_page) return;
      if (page === props.pagination.current_page) return;
      emit("paginate", page);
    }

    function onPerPage(e: Event) {
      emit("update:perPage", Number((e.target as HTMLSelectElement).value));
    }

    return { items, go, onPerPage };
  },
});
</script>

<style scoped>
.table-pager {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
  margin-top: 16px;
}
.table-pager__info {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  font-size: 13px;
}
.table-pager__size {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}
.table-pager__size select {
  width: auto;
  min-width: 64px;
}
.table-pager__count {
  white-space: nowrap;
}
.table-pager .pagination {
  flex-wrap: wrap;
  gap: 4px;
}
.table-pager .page-link.disabled {
  opacity: 0.4;
  pointer-events: none;
}
.table-pager__gap {
  pointer-events: none;
  border-color: transparent;
  background: transparent;
}
.table-pager__mobile {
  display: none;
}

@media (max-width: 575.98px) {
  .table-pager {
    justify-content: center;
    text-align: center;
  }
  .table-pager__info {
    justify-content: center;
  }
  .table-pager__page {
    display: none;
  }
  .table-pager__mobile {
    display: block;
  }
}
</style>
